/**
 * The booking and use terms a visitor accepts on the booking page, in English
 * and Arabic.
 *
 * The Arabic is the text MDS supplied. The English is translated from it and
 * follows it section by section; if the two are ever revised, revise both.
 *
 * Set `pdfUrl` once a PDF of the terms exists (the dialog shows a "Download
 * PDF" link only when it does).
 *
 * The contact section reads `CONTACT` rather than repeating the details, so
 * the terms always give the same phone, email and address as the rest of the
 * site.
 */

import { CONTACT } from './site';

export type PolicyLanguage = 'en' | 'ar';

export interface PolicySection {
  heading: string;
  /** One string per paragraph. May be empty when the section is only a list. */
  body: string[];
  /** A bulleted list, drawn after the paragraphs. */
  points?: string[];
}

export interface PolicyText {
  title: string;
  /** Paragraphs before the first section: what the document is and who it covers. */
  intro?: string[];
  sections: PolicySection[];
}

export interface Policy {
  /** A downloadable copy, if there is one. Null hides the link. */
  pdfUrl: string | null;
  text: Record<PolicyLanguage, PolicyText>;
}

export const BOOKING_POLICY: Policy = {
  pdfUrl: null,
  text: {
    en: {
      title: 'Booking and use terms',
      intro: [
        "These terms summarise the key rules for booking, entering and using Minieh Digital Square (MDS). They apply to customers, members, visitors, event organisers and guests. MDS's full policies contain further detail and are available on the MDS website. If a booking confirmation includes additional terms, those terms also apply to that booking.",
      ],
      sections: [
        {
          heading: '1. Acceptance and responsibility',
          body: [
            'By completing a booking, accepting these terms, entering MDS or using its services, you agree to follow these terms and the relevant full MDS policies. Anyone booking for a group or organisation must make sure all attendees and guests follow the rules. Booking and contact details must be accurate.',
          ],
        },
        {
          heading: '2. Bookings, packages and use of spaces',
          body: [],
          points: [
            'Desk package and room prices are set by the current price list and the booking confirmation.',
            'Daily, weekly, monthly, six-month and student packages are subject to their stated hours and benefits. All desk packages include 24-hour access, except the evening student package, which is limited to its published evening hours.',
            'A booking takes effect only once MDS has confirmed it and the required payment has been received.',
            'Desks and rooms may be used only for the confirmed purpose and duration. No other desk, room or restricted area may be used without approval.',
            'Room and event capacity depends on the approved setup and must not be exceeded. The café hall can be booked privately after 2:00 PM, subject to availability.',
            'Setup and cleaning time set by MDS is part of the booking. Extra charges may apply for overtime, cleaning, equipment or changes.',
            'Personal belongings remain your responsibility and must not be left where they create a hazard or get in the way of others.',
          ],
        },
        {
          heading: '3. Payment, cancellation and refunds',
          body: [],
          points: [
            'Desk packages and other bookings must be paid in full on confirmation, unless MDS agrees otherwise in writing.',
            'If MDS allows a balance to remain on a room or event booking, it must be paid before the booking starts, on the same day.',
            'Cancelling 7 or more calendar days before the booking: full refund.',
            'Cancelling 3 to 6 calendar days before the booking: 50% refund.',
            'Cancelling less than 3 calendar days before the booking, or not attending: no refund.',
            'Approved refunds are paid back by a reasonable, available method. Bank or payment provider processing times may apply.',
            'The customer pays for any damage, losses, exceptional cleaning, overtime or security call-outs caused by them, their attendees or their guests.',
          ],
        },
        {
          heading: '4. Respectful conduct and prohibited behaviour',
          body: [
            'MDS operates in Minieh, within a community shaped by Lebanese culture, family values, religious respect and the traditions of North Lebanon. Everyone must behave respectfully, use appropriate language and dress, keep noise to a reasonable level, and not disrupt work, prayer, meetings or community activities.',
          ],
          points: [
            'Harassment, threats, violence, discrimination, intimidation, inappropriate behaviour and seriously offensive language are not allowed.',
            'Alcohol, illegal drugs, weapons, gambling and any unlawful activity are not allowed.',
            'Smoking is allowed only in an area MDS sets aside for it.',
            'MDS property, furniture, equipment and facilities must be used with care and for their intended purpose.',
            'MDS may refuse entry, end a booking, remove a person, suspend their access or inform the authorities where reasonably necessary for safety or security, or because of serious misconduct.',
          ],
        },
        {
          heading: '5. Access cards, guests and after-hours access',
          body: [],
          points: [
            'Access cards may be issued to holders of weekly, monthly and six-month packages. Daily users may receive a temporary card.',
            'Cards, keys and access codes are personal and must not be shared. A fee of US$5 applies to each lost or unreturned card.',
            'After-hours users must keep doors secure, follow the closing instructions, not let in anyone unauthorised, and report any suspicious activity or security problem immediately.',
            'Guests must be registered or accompanied when asked, and the person who made the booking is responsible for their conduct.',
          ],
        },
        {
          heading: '6. Internet and technology use',
          body: [
            'The MDS network is provided for lawful and reasonable use. Café guests can use the guest network once they meet the current minimum purchase. Unauthorised access, spreading malware, disrupting networks or equipment, infringing intellectual property, downloading or sharing illegal material, or using the service in a way that harms others is not allowed. MDS may restrict or suspend access to protect its systems and users. You remain responsible for protecting your own devices, passwords and data.',
          ],
        },
        {
          heading: '7. Health, safety and emergencies',
          body: [],
          points: [
            'Follow staff instructions, safety signs, alarms and evacuation instructions immediately.',
            'Keep exits, stairs, corridors, doors and fire equipment clear, and do not tamper with alarms, extinguishers or safety systems.',
            'Report hazards, spills, injuries, illness, damage, near misses and emergencies promptly.',
            'In a serious emergency, call the appropriate local emergency service first, then inform MDS.',
            'Children must be supervised by a responsible adult, and event organisers must keep to the approved capacity and setup.',
          ],
        },
        {
          heading: '8. CCTV, security and privacy',
          body: [
            'CCTV may operate at entrances, exits, shared spaces and other security-sensitive locations for safety, security and the investigation of incidents. Cameras are not installed in toilets, prayer rooms or places where a reasonable degree of privacy is expected. Footage is viewed only for legitimate, authorised purposes, and may be handed over where reasonably necessary or where the law requires it.',
            'MDS may collect contact, identity, booking, attendance, access, payment reference, event, complaint and incident details, and basic website or internet information. This information is used to provide services, manage payment and access, communicate, protect safety, and meet operational or legal responsibilities. MDS does not sell personal information, and shares it only where reasonably needed with staff, service providers, advisers, emergency services or the authorities. MDS takes reasonable steps to protect information and keeps it only as long as reasonably necessary. Privacy questions or correction requests can be sent using the contact details below.',
          ],
        },
        {
          heading: '9. Photography, video and media',
          body: [
            'MDS may photograph or record public events, workshops and activities, giving reasonable notice where practical. Permission must be asked before taking a close-up photo, an interview, a testimonial or a recording in which a person is the clear subject. Promotional material showing anyone under 18 needs the consent of a parent or legal guardian. Recording in toilets, prayer rooms, private offices, closed meetings and restricted areas is not allowed without consent. If you do not wish to be photographed, tell the organiser or MDS staff. MDS will make reasonable efforts to respect this, but cannot guarantee you will be left out of every wide public shot.',
          ],
        },
        {
          heading: '10. Complaints, breaches and changes',
          body: [
            'Complaints or feedback can be given to staff, by phone, by email or in writing. MDS aims to acknowledge a written complaint within 2 working days and to tell you the outcome within 10 working days, or explain any reasonable delay. Serious safety matters must be reported immediately. Breaches may lead to a warning, charges, restricted access, removal, cancellation of a booking or referral to the authorities. MDS may update these terms; the version accepted at the time of booking stays on record for that booking.',
          ],
        },
        {
          heading: '11. Contacting MDS',
          body: [],
          points: [
            `Address: ${CONTACT.address.en}`,
            `Phone: ${CONTACT.phone}`,
            `Email: ${CONTACT.email}`,
          ],
        },
      ],
    },
    ar: {
      title: 'شروط الحجز والاستخدام في ساحة المنية الرقمية',
      intro: [
        'تلخص هذه الشروط الأحكام الأساسية للحجز والدخول واستخدام ساحة المنية الرقمية MDS، وتسري على العملاء والأعضاء والزوار ومنظمي الفعاليات والضيوف. تتضمن سياسات MDS الكاملة تفاصيل إضافية وهي متاحة على موقع MDS الإلكتروني. وإذا تضمن تأكيد حجز معين شروطاً إضافية، فتسري تلك الشروط أيضاً على ذلك الحجز.',
      ],
      sections: [
        {
          heading: '1. الموافقة والمسؤولية',
          body: [
            'بإتمام الحجز أو قبول هذه الشروط أو دخول MDS أو استخدام خدماتها، يوافق المستخدم على الالتزام بهذه الشروط وبسياسات MDS الكاملة ذات الصلة. وعلى من يحجز لمجموعة أو مؤسسة أن يضمن التزام جميع الحاضرين والضيوف بالقواعد. ويجب تقديم بيانات حجز واتصال صحيحة.',
          ],
        },
        {
          heading: '2. الحجوزات والباقات واستخدام المساحات',
          body: [],
          points: [
            'تُحدد أسعار باقات المكاتب والغرف وفق لائحة الأسعار الحالية وتأكيد الحجز.',
            'تخضع الباقات اليومية والأسبوعية والشهرية وباقة الستة أشهر وباقة الطلاب لساعاتها ومزاياها المحددة. وتشمل جميع باقات المكاتب دخولاً على مدار الساعة باستثناء باقة الطلاب المسائية، التي تقتصر على ساعاتها المسائية المعلنة.',
            'لا يصبح الحجز نافذاً إلا بعد تأكيده من MDS واستلام الدفعة المطلوبة.',
            'تُستخدم المكاتب والغرف فقط للغرض والمدة المؤكدين، ولا يجوز استخدام مكتب أو غرفة أو منطقة مقيدة أخرى دون موافقة.',
            'تعتمد سعة الغرف والفعاليات على التجهيز المعتمد ولا يجوز تجاوزها. ويمكن حجز قاعة المقهى بشكل خاص بعد الساعة 2:00 بعد الظهر حسب التوفر.',
            'تُعد مدة التجهيز والتنظيف التي تحددها MDS جزءاً من ترتيبات الحجز. وقد تُفرض رسوم إضافية على الوقت الإضافي أو التنظيف أو المعدات أو التغييرات.',
            'تبقى المتعلقات الشخصية على مسؤولية المستخدم، ولا يجوز تركها بما يسبب خطراً أو يعيق الآخرين.',
          ],
        },
        {
          heading: '3. الدفع والإلغاء والاسترداد',
          body: [],
          points: [
            'يجب دفع قيمة باقات المكاتب والحجوزات الأخرى كاملة عند التأكيد، ما لم توافق MDS خطياً على ترتيب آخر.',
            'إذا سمحت MDS برصيد متبقٍ لحجز غرفة أو فعالية، فيجب دفعه قبل بدء الحجز في اليوم نفسه.',
            'الإلغاء قبل 7 أيام تقويمية أو أكثر من موعد الحجز: استرداد كامل.',
            'الإلغاء قبل 3 إلى 6 أيام تقويمية من موعد الحجز: استرداد 50 بالمئة.',
            'الإلغاء قبل أقل من 3 أيام تقويمية أو عدم الحضور: لا يوجد استرداد.',
            'تُعاد المبالغ المعتمدة بطريقة معقولة ومتاحة، وقد تنطبق مهلة المصرف أو مزود الدفع.',
            'يتحمل العميل تكلفة الأضرار أو المفقودات أو التنظيف الاستثنائي أو الوقت الإضافي أو استدعاء الأمن الناتج عنه أو عن الحاضرين أو الضيوف.',
          ],
        },
        {
          heading: '4. السلوك المحترم والتصرفات الممنوعة',
          body: [
            'تعمل MDS في المنية ضمن مجتمع تحكمه الثقافة اللبنانية والقيم العائلية والاحترام الديني وتقاليد شمال لبنان. ويجب على الجميع التصرف باحترام، واستخدام لغة ولباس مناسبين، والحفاظ على مستوى معقول من الهدوء، وعدم تعطيل العمل أو الصلاة أو الاجتماعات أو الأنشطة المجتمعية.',
          ],
          points: [
            'تُمنع المضايقة والتهديد والعنف والتمييز والترهيب والسلوك غير اللائق واللغة المسيئة بصورة خطيرة.',
            'يُمنع الكحول والمخدرات غير القانونية والأسلحة والقمار وأي نشاط غير قانوني.',
            'لا يُسمح بالتدخين إلا في أي منطقة تحددها MDS لهذا الغرض.',
            'يجب استخدام ممتلكات MDS وأثاثها ومعداتها ومرافقها بعناية وللغرض المخصص لها.',
            'يجوز لـ MDS رفض الدخول أو إنهاء الحجز أو إخراج شخص أو تعليق دخوله أو إبلاغ الجهات المختصة عند الضرورة المعقولة للسلامة أو الأمن أو بسبب سوء سلوك جسيم.',
          ],
        },
        {
          heading: '5. بطاقات الدخول والضيوف والدخول بعد الدوام',
          body: [],
          points: [
            'يجوز إصدار بطاقات دخول لحاملي الباقات الأسبوعية والشهرية وباقة الستة أشهر، وقد يحصل المستخدم اليومي على بطاقة مؤقتة.',
            'البطاقات والمفاتيح ورموز الدخول شخصية ويُمنع مشاركتها. وتُفرض رسوم قدرها 5 دولارات أميركية عن كل بطاقة مفقودة أو غير معادة.',
            'على مستخدمي ما بعد الدوام إبقاء الأبواب آمنة، واتباع تعليمات الإغلاق، وعدم إدخال أشخاص غير مصرح لهم، والإبلاغ فوراً عن أي نشاط مشبوه أو مشكلة أمنية.',
            'يجب تسجيل الضيوف أو مرافقتهم عند الطلب، ويتحمل صاحب الحجز مسؤولية سلوكهم.',
          ],
        },
        {
          heading: '6. استخدام الإنترنت والتقنية',
          body: [
            'تُقدم شبكة MDS للاستخدام القانوني والمعقول. ويمكن لضيوف المقهى الحصول على شبكة الضيوف بعد استيفاء الحد الأدنى الحالي للشراء. ويُمنع الدخول غير المصرح به أو نشر البرامج الضارة أو تعطيل الشبكات أو المعدات أو انتهاك الملكية الفكرية أو تنزيل أو توزيع مواد غير قانونية أو استخدام الخدمة بما يضر الآخرين. ويجوز لـ MDS تقييد أو تعليق الدخول لحماية أنظمتها ومستخدميها. ويبقى المستخدم مسؤولاً عن حماية أجهزته وكلمات مروره وبياناته.',
          ],
        },
        {
          heading: '7. الصحة والسلامة والطوارئ',
          body: [],
          points: [
            'اتبع فوراً تعليمات الموظفين ولافتات السلامة والإنذارات وتعليمات الإخلاء.',
            'أبق المخارج والسلالم والممرات والأبواب ومعدات الحريق خالية، ولا تعبث بالإنذارات أو الطفايات أو أنظمة السلامة.',
            'أبلغ سريعاً عن المخاطر والانسكابات والإصابات والمرض والأضرار والحوادث التي كادت تقع والطوارئ.',
            'في الطوارئ الخطيرة، اتصل أولاً بخدمة الطوارئ المحلية المناسبة ثم أبلغ MDS.',
            'يجب أن يكون الأطفال تحت إشراف شخص بالغ مسؤول، وعلى منظمي الفعاليات الالتزام بالسعة والتجهيز المعتمدين.',
          ],
        },
        {
          heading: '8. كاميرات المراقبة والأمن والخصوصية',
          body: [
            'يجوز تشغيل كاميرات المراقبة عند المداخل والمخارج والمساحات المشتركة وغيرها من المواقع الحساسة أمنياً لأغراض السلامة والأمن والتحقيق في الحوادث. ولا تُركب الكاميرات في دورات المياه أو غرف الصلاة أو الأماكن التي يُتوقع فيها قدر معقول من الخصوصية. ولا يُطلع على التسجيلات إلا لأغراض مشروعة ومصرح بها، وقد تُسلم عند الضرورة المعقولة أو عندما يفرض القانون ذلك.',
            'يجوز لـ MDS جمع بيانات الاتصال والهوية والحجز والحضور والدخول ومراجع الدفع والفعاليات والشكاوى والحوادث ومعلومات أساسية عن الموقع الإلكتروني أو شبكة الإنترنت. وتُستخدم هذه البيانات لتقديم الخدمات وإدارة الدفع والدخول والتواصل وحماية السلامة والوفاء بالمسؤوليات التشغيلية أو القانونية. ولا تبيع MDS المعلومات الشخصية، ولا تشاركها إلا عند الحاجة المعقولة مع الموظفين أو مقدمي الخدمات أو المستشارين أو خدمات الطوارئ أو الجهات المختصة. وتتخذ MDS خطوات معقولة لحماية المعلومات وتحتفظ بها فقط للمدة اللازمة بشكل معقول. ويمكن تقديم أسئلة الخصوصية أو طلبات التصحيح عبر بيانات الاتصال أدناه.',
          ],
        },
        {
          heading: '9. التصوير والفيديو والإعلام',
          body: [
            'يجوز لـ MDS تصوير الفعاليات العامة وورش العمل والأنشطة أو تسجيلها بعد تقديم إشعار معقول متى كان ذلك عملياً. ويجب طلب الإذن قبل التقاط صورة قريبة أو إجراء مقابلة أو شهادة أو تسجيل يكون فيه الشخص هو الموضوع الواضح. وتتطلب المواد الترويجية التي يظهر فيها شخص دون 18 عاماً موافقة أحد الوالدين أو الوصي القانوني. ويُمنع التسجيل في دورات المياه وغرف الصلاة والمكاتب الخاصة والاجتماعات المغلقة والمناطق المقيدة دون موافقة. وعلى من لا يرغب في التصوير إبلاغ المنظم أو موظفي MDS، وستبذل MDS جهداً معقولاً لاحترام الرفض، لكن لا يمكن ضمان استبعاده من كل صورة عامة واسعة.',
          ],
        },
        {
          heading: '10. الشكاوى والمخالفات والتعديلات',
          body: [
            'يمكن تقديم الشكاوى أو الملاحظات إلى الموظفين أو بالهاتف أو البريد الإلكتروني أو خطياً. وتسعى MDS إلى تأكيد استلام الشكوى الخطية خلال يومي عمل وإبلاغ مقدمها بالنتيجة خلال 10 أيام عمل، أو توضيح أي تأخير معقول. ويجب الإبلاغ فوراً عن مسائل السلامة الخطيرة. وقد تؤدي المخالفات إلى إنذار أو رسوم أو تقييد الدخول أو الإخراج أو إلغاء الحجز أو إحالة الأمر إلى الجهات المختصة. ويجوز لـ MDS تحديث هذه الشروط، وسيبقى الإصدار المقبول وقت الحجز مسجلاً لذلك الحجز.',
          ],
        },
        {
          heading: '11. التواصل مع MDS',
          body: [],
          points: [
            `العنوان: ${CONTACT.address.ar}`,
            // Isolated left to right (U+2066…U+2069): in a right-to-left line
            // the number's groups would otherwise come out in reverse order.
            `الهاتف: ⁦${CONTACT.phone}⁩`,
            `البريد الإلكتروني: ${CONTACT.email}`,
          ],
        },
      ],
    },
  },
};
